import React, { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import { withNamespaces, WithNamespaces } from "react-i18next";
import format from "../../utils/formatter";
import Clear from "@material-ui/icons/Clear";
import { DialogProps } from "./dialogRouter";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Price } from '../main/motorcycles/MotorcycleView';
import { AddPartPayload } from "../../services/http/real";
import store from '../../redux/store';
import addPartAsync from '../../redux/async-actions/addPart';
import i18n from '../../i18n';
import Done from '@material-ui/icons/Done';
import { getPartById } from "../../utils/helpers";
import FadeInLetters from '../shared/FadeInLetters';

export interface ConflictingPartErrorResponse {
	ValidationResultType: number; // 0 invalid input,  1 rule exception
	InvalidInputMessage?: any; // if defined show error in a diferrent toast
	RulesResult: RulesResult;
}

export interface RulesResult {
	RuleType: RuleTypes;
	Part: ResponsePart;
	Parts: ResponsePart[];
	ChoosePart: boolean;
}

export enum RuleTypes {
	Required = 1,
	Conflicting = 0,
}

export interface ResponsePart {
	Code: string;
	Title: string;
	Price: Price;
	PreviewImageId: number;
	PreviewImage: string;
}

export interface RequiredPartsProps {
	rule: RulesResult;
	deleteRule?: true;

}

export interface ConflictingPartsProps {
	rule: RulesResult;
}

export interface RequiredSelectablePartsProps {
	rule: RulesResult;
	partKey: string;
}

type selectedPartIdsType = { [key: string]: string };

export const initialValue = false;

export const selectedPartIds$ = new BehaviorSubject<selectedPartIdsType | false>(initialValue);

export const addPartPayload$ = new BehaviorSubject<AddPartPayload | false>(initialValue);

export const ConflictingParts: React.FunctionComponent<DialogProps<ConflictingPartErrorResponse> & WithNamespaces> = ({ hideDialog, t, data }) => {

	// @todo add or remove selector
	const removeParts: boolean = data.RulesResult.RuleType === 0;
	const title = t(`dialogs.conflictingParts.${removeParts ? "conflicting" : "required"}.title`);
	const { Part: { Code } } = data.RulesResult;

	//use effect to subscribe and unsubsrcibe observable of selectable required parts
	useEffect(() => {

		// subscribe to the required streams
		const partIdsSub = selectedPartIds$.subscribe();

		// when this stream emits send values to the server
		const partPayloadSub = addPartPayload$
			.pipe(filter((val): val is AddPartPayload => !!val))
			.subscribe(val => {
				store.dispatch(addPartAsync(val) as any);
			});

		return function cleanup() {
			// values in this stream need reset during unmounting
			selectedPartIds$.next(initialValue);

			// cancel subscriptions
			partIdsSub.unsubscribe();
			partPayloadSub.unsubscribe();
		}
	}, []);

	const addPart = () => {
		// initialize the value of the payload with the value of observable (when not false) or the default
		let payload: AddPartPayload = addPartPayload$.getValue() || {
			PartId: Code,
			SelectedParts: [],
			RemoveParts: [],
		}

		if (data.RulesResult.RuleType === RuleTypes.Required) {
			// get all required selectable parts from the last value of the stream
			const selectedRequiredValues = selectedPartIds$.getValue();

			// if required selectable parts are not falsy add them otherwise provide empty array
			const requiredSelectedPartIds = selectedRequiredValues ? getRequiredPartsIds(selectedRequiredValues) : [];

			// if required non selectable parts are inside rules result add them otherwise provide empty array
			const requiredPreselectedIds = !data.RulesResult.ChoosePart ? getPartsIdsFromRulesResult(data.RulesResult) : [];

			// accumulates: required preselected, required selected and previously selected parts
			const SelectedParts = requiredPreselectedIds
				.concat(requiredSelectedPartIds)
				.concat(payload.SelectedParts)
				.filter((value, index, self) => self.indexOf(value) === index) // Contained duplicated Part codes which caused bug KPC-248; TODO: is most efficient solution?
				;

			if (SelectedParts.length) {
				payload = {
					...payload,
					SelectedParts,
				}
			}

		} else {
			// assumes RuleType.Conflicting

			// combines Remove part ids from this call and the previous one if any
			const RemoveParts = getPartsIdsFromRulesResult(data.RulesResult).concat(payload.RemoveParts);
			if (RemoveParts.length) {
				payload = {
					...payload,
					RemoveParts,
				}
			}
		}

		// emit next value to trigger server call
		addPartPayload$.next(payload);
	};

	const closeDialog = () => {
		// when user chooses to close dialog reset streams
		addPartPayload$.next(initialValue);
		selectedPartIds$.next(initialValue);
		hideDialog();
	}

	const ConflictingContent = withNamespaces()(ConflictingPartsContent);

	return (
		<Dialog
			disableBackdropClick={true}
			fullWidth={true}
			maxWidth={"md"}
			open={true}
			onClose={hideDialog}
			aria-labelledby="conflicting-parts-modal"
			id="conflicting-parts-dialog"
		>
			<DialogTitle id="conflicting-parts-dialog-title">
				<Grid container justify="space-between" direction="row">
					<h2
						id="conflicting-parts-modal"
						className="conflicting-part-headline underline uppercase"
					>
						<FadeInLetters text={title} />
					</h2>
					<IconButton className="dialog-hide-icon" onClick={closeDialog}>
						<Clear />
					</IconButton>
				</Grid>
			</DialogTitle>
			<DialogContent>
				<ConflictingContent {...data} />
			</DialogContent>
			<DialogActions>
				<button onClick={closeDialog} className="secondary m-r-5">
					{t("buttons.cancel")}
				</button>
				<button onClick={addPart} className="primary m-r-5">
					{t("buttons.confirmPartRules")}
				</button>
			</DialogActions>
		</Dialog >
	)
}

export const ConflictingPartsContent: React.FunctionComponent<ConflictingPartErrorResponse & WithNamespaces> = ({ RulesResult, t }) => {
	const remove = RulesResult.RuleType === RuleTypes.Conflicting;
	return (
		remove ? <PartsToRemove rule={RulesResult} />
			: <RequiredParts rule={RulesResult} />
	)
}

export const RequiredParts: React.FunctionComponent<RequiredPartsProps> = ({ rule, deleteRule }) => {
	const { Part: { Title, PreviewImage, Price } } = rule;
	const { ChoosePart: isSelectable } = rule;
	const selectable = <>
		<span className="required-part-info">{i18n.t("dialogs.conflictingParts.required.selectRequired")}</span>
		<RequiredSelectableParts rule={rule} partKey={`selectable`} />
	</>;

	const nonSelectable = <>
		<span className="required-part-info">{i18n.t(`dialogs.conflictingParts.required.${deleteRule ? 'delete' : 'requiredNonSelectable'}`)}</span>
		<RequiredNonSelectableParts parts={rule.Parts} />
	</>
	return (
		<Grid container>
			<Grid item xs={4}>
				<div className="dialog-image-wrapper">
					<Grid
						container
						justify="space-between"
						alignItems="flex-end"
						className="part-item part-item-big orange-border"
					>
						<Grid item xs={12}>
							<h3>{Title}</h3>
						</Grid>
						<Grid item xs={12}>
							<img
								className="fitting-image big-image"
								src={PreviewImage}
								alt={Title}
							/>
						</Grid>
						<Grid container justify="flex-end">
							<Grid item className="part-item-price">
								{format.currency(Price)}
							</Grid>
						</Grid>
					</Grid>
				</div>
			</Grid>
			<Grid item xs={8}>
				{isSelectable ? selectable : nonSelectable}
			</Grid>
		</Grid>
	);
}

export const RequiredSelectableParts: React.FunctionComponent<RequiredSelectablePartsProps> = ({ rule, partKey }) => {
	const { Parts } = rule;
	const [{ Code }] = Parts;
	const [selected, setSelected] = useState<false | string>(false);

	useEffect(() => {
		selectedPartIds$.next({ [partKey]: Code });
		const subscription = selectedPartIds$.subscribe((value) => {
			// when stream emits make sure to refresh component
			setSelected((value as selectedPartIdsType)[partKey]);
		})
		return function cleanup() {
			// unsubscribe on component destruction
			subscription.unsubscribe()
		}
	}, [partKey, Code]);

	const selectPart = (Code: string) => {
		const values = selectedPartIds$.getValue();
		selectedPartIds$.next({
			...values,
			[partKey]: Code,
		});
	}

	const partsInGrid = Parts.map(({ PreviewImage, Title, Price, Code }: ResponsePart, idx: number) => {
		const higlighted = Code === selected;

		return (
			<Grid key={idx} onClick={selectPart.bind(null, Code)}
				container
				alignItems="center"
				className={`part-item clickable ${higlighted ? "orange-border" : "transparent-border"}`}
			>
				<Grid item xs={1} style={{ visibility: higlighted ? "visible" : "hidden" }}>
					<div className="selected-icon-wrapper">
						<Done color="primary" />
					</div>
				</Grid>
				<Grid item xs={2}>
					<img
						className="fitting-image small-image"
						src={PreviewImage}
						alt={Title}
					/>
				</Grid>
				<Grid item className="part-item-title" xs={6}>
					<h3>{Title}</h3>
				</Grid>
				<Grid item className="part-item-price text-center" xs={3}>
					{format.currency(Price)}
				</Grid>
			</Grid>
		)
	})

	return <>{partsInGrid}</>;
}

export const RequiredNonSelectableParts: React.FunctionComponent<{ parts: ResponsePart[] }> = ({ parts }) => {
	return (
		<>
			{parts.map((part: ResponsePart, idx: number) => {
				const { PreviewImage, Title, Price } = part;

				return (<Grid container key={idx} justify="space-around" alignItems="center">
					<Grid item xs={1}></Grid>
					<Grid item xs={2}>
						<img alt="part" className="fitting-image small-image" src={PreviewImage} />
					</Grid>
					<Grid item xs={6}><h3>{Title}</h3></Grid>
					<Grid item xs={3} className="part-item-price">{format.currency(Price)}</Grid>
				</Grid>
				)
			})}
		</>
	)
}

export default withNamespaces()(ConflictingParts);

export const PartsToRemove: React.FunctionComponent<ConflictingPartsProps> = ({ rule }) => {
	//old parts
	const { Parts }: RulesResult = rule;

	// the part to replace old parts
	const { Part } = rule;

	return (
		<Grid container justify="space-between" spacing={16}>
			<Grid item xs={6}><ReplacePart replacing={true} part={Part} /></Grid>
			<Grid item xs={6}>{Parts.map((part: ResponsePart, idx: number) => <ReplacePart replacing={false} part={part} key={idx} />)}</Grid>
		</Grid>
	)
}

export interface ReplacePartsProps {
	part: ResponsePart;
	replacing: boolean;
}

export const ReplacePart: React.FunctionComponent<ReplacePartsProps> = ({ part: { Title, PreviewImage, Price, Code }, replacing }) => {
	const message = i18n.t(`dialogs.conflictingParts.conflicting.${replacing ? 'replaceBy' : 'currentlySelected'}`);
	return (
		<Grid container className={`replace-part-wrapper ${replacing ? "grey-border" : "orange-border"}`}>
			<div className="replace-part-message">{message}</div>
			<Grid container alignItems="center">
				<Grid item xs={6}>
					<div className="replace-part-image-wrapper">
						<img alt="part" className="fitting-image" src={PreviewImage} />
					</div>
				</Grid>
				<Grid item xs={6}><h3>{Title}</h3> </Grid>
			</Grid>
			<Grid container className="part-item-price larger-font" justify="flex-end">
				{format.currency(Price ? Price : getPartById(Code).Price)}
			</Grid>
		</Grid>
	)
}

function getPartsIdsFromRulesResult(rulesResult: RulesResult): string[] {
	return rulesResult.Parts.reduce((acc: string[], part: ResponsePart) => [...acc, part.Code], []);
}

function getRequiredPartsIds(selectedRequiredValues: selectedPartIdsType | false) {
	if (!selectedRequiredValues) {
		return [];
	}
	return Object
		.keys(selectedRequiredValues)
		.reduce((acc: string[], selectionKeys: string) => [...acc, selectedRequiredValues[selectionKeys]], []);
}
